import { createElement, forwardRef } from "react";

import { Icon } from "../icon/Icon";
import { Link } from "../link/Link";
import { Spinner } from "../spinner/Spinner";

import "./Button.css";

import type { Ref, ReactNode, CSSProperties, SyntheticEvent } from "react";
import type { SizeProp } from "../components";
import type { IconVariantProp } from "../icon/Icon";
import type { LinkProps } from "../link/Link";

export interface ButtonProps {
  active?: boolean;
  appearance?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "control"
    | "minimal"
    | "link";
  busy?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  fill?: boolean;
  hidden?: boolean;
  href?: string;
  icon?: string;
  iconPosition?: "top" | "bottom" | "right" | "left";
  iconVariant?: IconVariantProp;
  invert?: boolean;
  label?: string;
  nativeBehavior?: LinkProps["nativeBehavior"];
  nativeStencil?: LinkProps["nativeStencil"];
  reloadDocument?: boolean;
  shape?: "square" | "circle";
  size?: Extract<SizeProp, "xs" | "sm" | "md" | "lg">;
  style?: CSSProperties;
  target?: string;
  text?: string;
  value?: string;
  onClick?: (e: SyntheticEvent) => void;
}

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(function ButtonImpl(
  {
    active,
    appearance = "primary",
    busy,
    children,
    disabled,
    fill,
    hidden,
    href,
    icon,
    iconPosition,
    iconVariant,
    invert,
    label,
    nativeBehavior,
    nativeStencil,
    reloadDocument,
    shape,
    size,
    style,
    text,
    value,
    onClick,
    ...passThrough
  },
  ref,
) {
  const Children = (
    <>
      {icon && (
        <span className={"Button__icon"}>
          <Icon
            icon={icon}
            variant={iconVariant}
            size={size === "xs" ? "sm" : size}
          />
        </span>
      )}

      {Boolean(children || text) && (
        <span className="Button__text">
          {text}
          {children}
        </span>
      )}

      {busy && (
        <div className="Button__spinner">
          <Spinner size={size} invert={!invert} />
        </div>
      )}
    </>
  );

  const baseProps = {
    ...passThrough,
    "aria-label": label,
    "aria-pressed": Boolean(active),
    "data-appearance": appearance,
    "data-busy": busy,
    "data-fill": fill,
    "data-icon-position": iconPosition,
    "data-invert": invert,
    "data-shape": shape,
    "data-size": size,
    children: Children,
    className: "Button",
    disabled: disabled || busy,
    hidden,
    style,
    onClick,
  };

  return href
    ? createElement(Link, {
        ...baseProps,
        to: href,
        nativeBehavior,
        nativeStencil,
        ref: ref as Ref<HTMLAnchorElement>,
        reloadDocument,
      })
    : createElement("button", {
        ...baseProps,
        value,
        ref: ref as Ref<HTMLButtonElement>,
      });
});
